// The Smart CMS section that's open: Articles, the Service Map or Site Config. Its tools edit the page's live
// store, so the page switches it on with useSmartCmsAssistant and passes that store in

import { useCallback, useRef } from "react"
import { useAssistant, useAssistantModule } from "@/components/assistant/context"
import type { AssistantModule, ModuleCatalogEntry, ToolPolicy } from "@/lib/assistant/types"
import { atLeast, isReadOnlyTool, isSelfGatedTool, type CmsAccess } from "@/pages/dev/smart-cms/shared/permissions"
import { PendingEditReview } from "@/pages/dev/smart-cms/shared/components/PendingEditReview"
import { ImportReview } from "@/pages/dev/smart-cms/service-map/ImportReview"
import type { ImportPlan, ParsedSheet } from "@/pages/dev/smart-cms/service-map/import"
import { nodeTitle } from "@/pages/dev/smart-cms/articles/types"
import type { useLocalCMS } from "@/pages/dev/smart-cms/articles/useLocalCMS"
import { makeCMSTools, CMS_TOOL_LABELS } from "./articles/tools"
import * as cmsStore from "@/pages/dev/smart-cms/articles/store"
import type { useServiceCMS } from "@/pages/dev/smart-cms/service-map/useServiceCMS"
import { makeServiceTools, SERVICE_TOOL_LABELS } from "./serviceMap/tools"
import * as svcStore from "@/pages/dev/smart-cms/service-map/store"
import type { useSiteConfigCMS } from "@/pages/dev/smart-cms/site-config/useSiteConfigCMS"
import { makeSiteConfigTools, SITECONFIG_TOOL_LABELS } from "./siteConfig/tools"
import * as siteconfigStore from "@/pages/dev/smart-cms/site-config/store"
import { makeWorkflowTools, WORKFLOW_TOOL_LABELS, type WorkflowToolContext } from "./workflow/tools"
import { sectionInstructions } from "./prompt"

export type SmartCmsBackend = "cms" | "services" | "siteconfig"

const BACKEND_MODULE: Record<SmartCmsBackend, { id: string; title: string }> = {
  cms: { id: "cms.articles", title: "Smart CMS: Articles" },
  services: { id: "cms.serviceMap", title: "Smart CMS: Service Map" },
  siteconfig: { id: "cms.siteConfig", title: "Smart CMS: Site Configuration" },
}

export const smartCmsCatalog: ModuleCatalogEntry[] = [
  {
    id: "cms.articles",
    title: "Smart CMS: Articles",
    route: "/smart-cms?open=articles",
    description: "Help-center categories, sections and articles, their translations and publishing.",
    resource: "cms",
  },
  {
    id: "cms.serviceMap",
    title: "Smart CMS: Service Map",
    route: "/smart-cms?open=services",
    description: "Services, providers, geography and taxonomy, translations, spreadsheet imports.",
    resource: "cms",
  },
  {
    id: "cms.siteConfig",
    title: "Smart CMS: Site Configuration",
    route: "/smart-cms?open=site",
    description: "Public sites: settings, header and footer navigation, page content blocks.",
    resource: "cms",
  },
]

// Read-only tools are reads, the rest are writes; some already ask for approval themselves
function cmsPolicies(tools: Record<string, unknown>): Record<string, ToolPolicy> {
  return Object.fromEntries(Object.keys(tools).map(name => [name, isReadOnlyTool(name)
    ? { kind: "read" as const }
    : { kind: /delete|workflow_(publish|discard)_changes/.test(name) ? "destructive" as const : "write" as const, selfReviewed: isSelfGatedTool(name) }]))
}

// Each section's tool factory, for the strict-schema check
export const SMART_CMS_TOOL_FACTORIES: Record<string, (ctx: any) => Record<string, any>> = {
  "cms.articles": ctx => ({ ...makeCMSTools(ctx), ...makeWorkflowTools(ctx) }),
  "cms.serviceMap": ctx => ({ ...makeServiceTools(ctx), ...makeWorkflowTools(ctx) }),
  "cms.siteConfig": ctx => ({ ...makeSiteConfigTools(ctx), ...makeWorkflowTools(ctx) }),
}

export interface SmartCmsAssistantDeps {
  backend: SmartCmsBackend
  // The role's access to the open section
  access: CmsAccess
  cms: ReturnType<typeof useLocalCMS>
  svc: ReturnType<typeof useServiceCMS>
  siteconfig: ReturnType<typeof useSiteConfigCMS>
  setEditorOpen: (open: boolean) => void
  // The spreadsheet uploaded for import, read when a tool runs
  getSheet: () => ParsedSheet | null
  // What a site can be scoped to, from the other two sections
  siteconfigCountries: Array<{ id: string; name: string }>
  siteconfigRegions: Array<{ id: string; name: string }>
  siteconfigCities: Array<{ id: string; name: string }>
  siteconfigArticleCategories: Array<{ id: string; name: string }>
  // Reviews, owners, publishing, filters and media for the open section
  workflow: WorkflowToolContext
}

export function useSmartCmsAssistant({
  backend, access, cms, svc, siteconfig, setEditorOpen, getSheet,
  siteconfigCountries, siteconfigRegions, siteconfigCities, siteconfigArticleCategories, workflow,
}: SmartCmsAssistantDeps) {
  const isCMS = backend === "cms"
  const isSvc = backend === "services"
  const isSiteConfig = backend === "siteconfig"

  // Serializes approval prompts: the model can emit several destructive calls in one step
  const cmsHitlQueue = useRef<Promise<void>>(Promise.resolve())

  // Approvals show in the assistant panel, using the CMS diff card
  const { requestCustom, autoAccept } = useAssistant()
  const requestCMSApproval = useCallback((req: {
    summary: string
    titleBefore?: string
    titleAfter?: string
    bodyBefore?: string
    bodyAfter?: string
    locale?: string
  }): Promise<boolean> => {
    if (autoAccept) return Promise.resolve(true)
    // Body fields must be strings: the diff view splits them
    const data = {
      article_id: 0,
      title_before: req.titleBefore ?? req.summary,
      title_after: req.titleAfter ?? req.summary,
      body_before_md: req.bodyBefore ?? "",
      body_after_md: req.bodyAfter ?? "",
      locale: req.locale,
      note: req.summary,
    }
    return requestCustom<boolean>(done => (
      <PendingEditReview data={data} onApprove={() => done(true)} onReject={() => done(false)} destinationLabel="the Smart CMS" />
    ), false)
  }, [autoAccept, requestCustom])

  const requestImportApproval = useCallback((args: { sheetName: string; rowCount: number; plan: ImportPlan; limit?: number }): Promise<string[] | null> => {
    if (autoAccept) return Promise.resolve(args.plan.newValues.filter(v => !v.similarTo).map(v => v.key))
    return requestCustom<string[] | null>(done => (
      <ImportReview
        data={{ sheetName: args.sheetName, rowCount: args.rowCount, plan: args.plan, limit: args.limit }}
        onApprove={keys => done(keys)}
        onReject={() => done(null)}
      />
    ), null)
  }, [autoAccept, requestCustom])

  const createCMSTools = useCallback(() => ({
    ...makeCMSTools({
    getStore: cms.getStore,
    setStore: cms.setStore,
    locale: cms.locale,
    onNodeFocused: (id) => cms.setSelectedId(id),
    onRequestApproval: requestCMSApproval,
    record: cms.record,
    getHistory: cms.getHistory,
    revert: cms.revert,
    versionsOf: cms.versionsOf,
    restoreNodeVersion: cms.restoreNodeVersion,
    autoAccept,
    }, cmsHitlQueue),
  }), [
    cms.getStore, cms.setStore, cms.locale, cms.setSelectedId,
    cms.record, cms.getHistory, cms.revert, cms.versionsOf, cms.restoreNodeVersion,
    requestCMSApproval, autoAccept,
  ])

  // Service-mapping tools share the review card and the same queueing rationale
  // as the CMS ones; only the (summary, detail) shape differs, so map detail
  // onto the diff body.
  const svcHitlQueue = useRef<Promise<void>>(Promise.resolve())

  const createServiceTools = useCallback(() => ({
    ...makeServiceTools({
      getStore: svc.getStore,
      setStore: svc.setStore,
      locale: svc.locale,
      onEntityFocused: (kind, id) => { svc.setSelected({ kind, id }); setEditorOpen(true) },
      getSheet: () => getSheet(),
      // Delete can remove the entity the editor is showing, either directly or
      // by cascading from a deleted parent (a country taking its regions and
      // cities with it). Either way, check whether the current selection still
      // resolves and close the editor if it does not — otherwise it keeps
      // showing a dead record's "no longer exists" panel indefinitely.
      onEntityDeleted: () => {
        const cur = svc.selected
        if (cur && !svcStore.getEntity(svc.getStore(), cur.kind, cur.id)) {
          svc.setSelected(null)
          setEditorOpen(false)
        }
      },
      onRequestApproval: ({ summary, detail }) =>
        requestCMSApproval({ summary, bodyBefore: "", bodyAfter: detail ?? "" }),
      onRequestImportApproval: requestImportApproval,
      autoAccept,
      record: svc.record,
      recordMany: svc.recordMany,
      recordBulk: svc.recordBulk,
      getHistory: svc.getHistory,
      revert: svc.revert,
      versionsOf: svc.versionsOf,
      restoreEntityVersion: svc.restoreEntityVersion,
    }, svcHitlQueue),
  }), [
    svc.getStore, svc.setStore, svc.locale, svc.setSelected, svc.selected,
    svc.record, svc.recordMany, svc.recordBulk, svc.getHistory, svc.revert, svc.versionsOf, svc.restoreEntityVersion,
    requestCMSApproval, requestImportApproval, autoAccept,
  ]) // uploadedSheetRef is read at call time, not a dependency

  // Site-config tools share the same review card and queueing rationale as
  // the CMS/Service ones.
  const siteconfigHitlQueue = useRef<Promise<void>>(Promise.resolve())

  const createSiteConfigTools = useCallback(() => ({
    ...makeSiteConfigTools({
      getStore: siteconfig.getStore,
      setStore: siteconfig.setStore,
      locale: siteconfig.locale,
      onEntityFocused: (kind, id) => { siteconfig.setSelected({ kind, id }); setEditorOpen(true) },
      onEntityDeleted: () => {
        const cur = siteconfig.selected
        if (cur && !siteconfigStore.getEntity(siteconfig.getStore(), cur.kind, cur.id)) {
          siteconfig.setSelected(null)
          setEditorOpen(false)
        }
      },
      onRequestApproval: ({ summary, detail }) =>
        requestCMSApproval({ summary, bodyBefore: "", bodyAfter: detail ?? "" }),
      autoAccept,
      record: siteconfig.record,
      getHistory: siteconfig.getHistory,
      revert: siteconfig.revert,
      versionsOf: siteconfig.versionsOf,
      restoreEntityVersion: siteconfig.restoreEntityVersion,
    }, siteconfigHitlQueue),
  }), [
    siteconfig.getStore, siteconfig.setStore, siteconfig.locale, siteconfig.setSelected, siteconfig.selected,
    siteconfig.record, siteconfig.getHistory, siteconfig.revert, siteconfig.versionsOf, siteconfig.restoreEntityVersion,
    requestCMSApproval, autoAccept,
  ])

  // The open section is an assistant module: its instructions, what's on screen, and its tools
  useAssistantModule(BACKEND_MODULE[backend].id, (): AssistantModule => {
    const tools: Record<string, any> = {
      ...(isSvc ? createServiceTools() : isCMS ? createCMSTools() : createSiteConfigTools()),
      ...makeWorkflowTools(workflow),
    }
    const instructions = sectionInstructions({
      backend, cms, svc, siteconfig, getSheet,
      siteconfigCountries, siteconfigRegions, siteconfigCities, siteconfigArticleCategories,
    })

    // What's open in the editor, so "this article" or "this service" resolves
    const selection = isCMS && cms.selectedId
      ? `Open in the editor: ${nodeTitle(cmsStore.getNode(cms.store, cms.selectedId) ?? ({ translations: {} } as any), cms.locale, cms.store.defaultLocale)} (id ${cms.selectedId})`
      : isSvc && svc.selected
        ? `Open in the editor: ${svc.selected.kind} ${svc.selected.id}`
        : isSiteConfig && siteconfig.selected
          ? `Open in the editor: ${siteconfig.selected.kind} ${siteconfig.selected.id}`
          : "Nothing open in the editor."

    return {
      id: BACKEND_MODULE[backend].id,
      title: BACKEND_MODULE[backend].title,
      tools,
      policies: cmsPolicies(tools),
      writable: atLeast(access, "edit"),
      instructions,
      context: `${selection}\nYour access here: ${access}.${atLeast(access, "manage") ? "" : " Published content is locked for editors; deletes need a manager."}`,
      labels: { ...CMS_TOOL_LABELS, ...SERVICE_TOOL_LABELS, ...SITECONFIG_TOOL_LABELS, ...WORKFLOW_TOOL_LABELS },
    }
  }, atLeast(access, "view"))
}
